import { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { toast } from "react-toastify";
import {
  listExternalLabClinicOptions,
  listExternalLabMappings,
  mapExternalLabToClinics,
  unlinkExternalLabMapping,
} from "api/externalLabs";
import { formatDate } from "utils/externalLabRules";
import ExternalLabActionDialog from "./ExternalLabActionDialog";

const clinicLabel = (clinic) =>
  [clinic?.name, clinic?.city].filter(Boolean).join(" · ") || "Unnamed clinic";

const ExternalLabMappingSection = ({ labId, canMap }) => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUnlinked, setShowUnlinked] = useState(false);
  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [unlinking, setUnlinking] = useState(null);

  const loadMappings = useCallback(async () => {
    setLoading(true);
    const res = await listExternalLabMappings(labId, showUnlinked);
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error);
      setMappings([]);
      return;
    }
    setMappings(res.data || []);
  }, [labId, showUnlinked]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  const loadOptions = useCallback(async () => {
    if (!canMap) return;
    setOptionsLoading(true);
    const res = await listExternalLabClinicOptions(labId, search.trim());
    setOptionsLoading(false);
    setOptions(res.ok ? res.data || [] : []);
  }, [labId, search, canMap]);

  useEffect(() => {
    const timer = setTimeout(loadOptions, 300);
    return () => clearTimeout(timer);
  }, [loadOptions]);

  const mapSelected = async () => {
    if (!selected.length) return;
    setSaving(true);
    const res = await mapExternalLabToClinics(
      labId,
      selected.map((clinic) => clinic._id),
    );
    setSaving(false);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    toast.success(
      selected.length === 1
        ? "Hospital / clinic mapped"
        : `${selected.length} hospitals / clinics mapped`,
    );
    setSelected([]);
    loadMappings();
    loadOptions();
  };

  const unlink = async () => {
    setSaving(true);
    const res = await unlinkExternalLabMapping(labId, unlinking._id);
    setSaving(false);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    toast.success("Hospital / clinic unlinked");
    setUnlinking(null);
    loadMappings();
    loadOptions();
  };

  return (
    <Box data-testid="lab-mapping-section">
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
          Mapped hospitals / clinics
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showUnlinked}
              onChange={(event) => setShowUnlinked(event.target.checked)}
            />
          }
          label={<Typography sx={{ fontSize: 12.5 }}>Show unlinked</Typography>}
        />
      </Box>

      {!canMap ? (
        <Typography sx={{ fontSize: 12.5, color: "#6B7280", mb: 1 }}>
          Only an approved, active lab can be mapped to a hospital / clinic.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          <Box sx={{ flex: "1 1 280px", minWidth: 0 }}>
            <Autocomplete
              multiple
              size="small"
              options={options}
              value={selected}
              loading={optionsLoading}
              filterOptions={(list) => list}
              getOptionLabel={clinicLabel}
              isOptionEqualToValue={(a, b) => a._id === b._id}
              onChange={(_, value) => setSelected(value)}
              onInputChange={(_, value, reason) => {
                if (reason === "input") setSearch(value);
              }}
              noOptionsText="No unmapped hospitals / clinics found"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select hospitals / clinics"
                  placeholder="Search by name"
                />
              )}
            />
          </Box>
          <Button
            variant="contained"
            onClick={mapSelected}
            disabled={!selected.length || saving}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {saving ? "Mapping…" : "Map"}
          </Button>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : mappings.length === 0 ? (
        <Typography
          sx={{
            p: 2,
            textAlign: "center",
            border: "1px dashed #D1D5DB",
            borderRadius: 2,
            fontSize: 12.5,
            color: "#6B7280",
            bgcolor: "#fff",
          }}
        >
          Not mapped to any hospital / clinic.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {mappings.map((mapping) => (
            <Box
              key={mapping._id}
              data-testid="lab-mapping-row"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1,
                borderRadius: 2,
                border: "1px solid #E5E7EB",
                bgcolor: mapping.isActive ? "#fff" : "#F9FAFB",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>
                  {mapping.clinicName || "Unknown clinic"}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
                  {[mapping.clinicType, mapping.clinicCity].filter(Boolean).join(" · ")}
                  {mapping.isActive
                    ? ` · Mapped ${formatDate(mapping.mappedAt)}`
                    : ` · Unlinked ${formatDate(mapping.unmappedAt)}`}
                </Typography>
              </Box>
              {mapping.isActive ? (
                <Tooltip title="Unlink">
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={`Unlink ${mapping.clinicName}`}
                    onClick={() => setUnlinking(mapping)}
                  >
                    <LinkOffIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Chip size="small" label="Unlinked" sx={{ height: 20, fontSize: 11 }} />
              )}
            </Box>
          ))}
        </Box>
      )}

      <ExternalLabActionDialog
        open={Boolean(unlinking)}
        title="Unlink this hospital / clinic?"
        description={`"${unlinking?.clinicName}" will no longer be associated with this lab. The mapping is kept as history and can be re-mapped later.`}
        confirmLabel="Unlink"
        confirmColor="error"
        busy={saving}
        onCancel={() => setUnlinking(null)}
        onConfirm={unlink}
      />
    </Box>
  );
};

export default ExternalLabMappingSection;
